import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, ListGroup } from 'react-bootstrap';
import type { Banner } from '../../../../../apps/hooks/banner/useBanner';

interface BannerOrderModalProps {
  show: boolean;
  onHide: () => void;
  banners: Banner[];
  onSaveOrder: (orderedBanners: Array<{ bannerId: string; order: number }>) => void;
  isLoading: boolean;
}

interface DraggableBanner extends Banner {
  tempOrder: number;
}

const BannerOrderModal: React.FC<BannerOrderModalProps> = ({ show, onHide, banners, onSaveOrder, isLoading }) => {
  const [orderedBanners, setOrderedBanners] = useState<DraggableBanner[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Initialize ordered banners when modal opens or banners change
  useEffect(() => {
    if (show && banners.length > 0) {
      // Sort banners by current order, then by title for consistent ordering
      const sorted = [...banners]
        .sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          return a.title.localeCompare(b.title);
        })
        .map((banner, index) => ({
          ...banner,
          tempOrder: index + 1,
        }));
      setOrderedBanners(sorted);
    }
  }, [show, banners]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newBanners = [...orderedBanners];
    const draggedBanner = newBanners[draggedIndex];

    if (!draggedBanner) {
      setDraggedIndex(null);
      return;
    }

    // Remove dragged item
    newBanners.splice(draggedIndex, 1);
    // Insert at new position
    newBanners.splice(dropIndex, 0, draggedBanner);

    // Update temp orders
    const reorderedBanners = newBanners.map((banner, index) => ({
      ...banner,
      tempOrder: index + 1,
    }));

    setOrderedBanners(reorderedBanners);
    setDraggedIndex(null);
  };

  const handleSave = () => {
    const orderUpdates = orderedBanners.map((banner) => ({
      bannerId: banner.bannerId,
      order: banner.tempOrder,
    }));
    onSaveOrder(orderUpdates);
  };

  const moveUp = (index: number) => {
    if (index === 0) {
      return;
    }

    const newBanners = [...orderedBanners];
    const current = newBanners[index];
    const previous = newBanners[index - 1];

    if (!current || !previous) {
      return;
    }

    newBanners[index - 1] = current;
    newBanners[index] = previous;

    const reorderedBanners = newBanners.map((banner, i) => ({
      ...banner,
      tempOrder: i + 1,
    }));

    setOrderedBanners(reorderedBanners);
  };

  const moveDown = (index: number) => {
    if (index === orderedBanners.length - 1) {
      return;
    }

    const newBanners = [...orderedBanners];
    const current = newBanners[index];
    const next = newBanners[index + 1];

    if (!current || !next) {
      return;
    }

    newBanners[index] = next;
    newBanners[index + 1] = current;

    const reorderedBanners = newBanners.map((banner, i) => ({
      ...banner,
      tempOrder: i + 1,
    }));

    setOrderedBanners(reorderedBanners);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manage Banner Order</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="info" className="mb-3">
          <strong>Instructions:</strong>
          <ul className="mb-0 mt-2">
            <li>Drag and drop banners to reorder them</li>
            <li>Use the arrow buttons to move banners up or down</li>
            <li>Order 1 appears first, Order 2 appears second, etc.</li>
            <li>Click &quot;Save Order&quot; to apply changes</li>
          </ul>
        </Alert>

        {orderedBanners.length === 0 ? (
          <div className="text-center py-4">
            <p className="text-muted">No banners to order</p>
          </div>
        ) : (
          <ListGroup>
            {orderedBanners.map((banner, index) => (
              <ListGroup.Item
                key={banner.bannerId}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                className={`d-flex align-items-center justify-content-between ${
                  draggedIndex === index ? 'opacity-50' : ''
                }`}
                style={{ cursor: 'grab' }}
              >
                <div className="d-flex align-items-center flex-grow-1">
                  <div className="me-3">
                    <span className="badge bg-primary fs-6">#{banner.tempOrder}</span>
                  </div>
                  <div className="me-3">
                    {banner.imageUrl && (
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        style={{ width: '60px', height: '36px', objectFit: 'cover' }}
                        className="rounded"
                      />
                    )}
                  </div>
                  <div className="flex-grow-1">
                    <div className="fw-bold">{banner.title}</div>
                    <div className="text-muted small">
                      Location: {banner.location} | Current Order: {banner.order}
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-1">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    title="Move up"
                  >
                    ↑
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => moveDown(index)}
                    disabled={index === orderedBanners.length - 1}
                    title="Move down"
                  >
                    ↓
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isLoading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isLoading || orderedBanners.length === 0}>
          {isLoading ? 'Saving...' : 'Save Order'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default BannerOrderModal;
